import React, { createContext, useContext, useEffect, useState } from "react";
import {
  addToCartAPI,
  deleteCart,
  getCart,
  updateCartQuantityAPI,
} from "../api/cartApi";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import { showQuantityToast } from "../utils/toast";

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const { isLoggedIn } = useAuth();

  // Centralized fetchCart function with proper error handling
  const fetchCart = async () => {
    try {
      if (isLoggedIn) {
        const serverCartResponse = await getCart();
        // Ensure cartDetails is properly formatted
        const formattedCart = (serverCartResponse?.cartDetails || []).map(item => ({
          ...item,
          size: item.size || "", 
          cart_id: item.cart_id || null // Ensure cart_id is properly set
        }));
        setCartItems(formattedCart);
      } else {
        const guestCart = JSON.parse(localStorage.getItem("guestCart") || "[]");
        // Format guest cart items to match server structure
        const formattedGuestCart = guestCart.map(item => ({
          ...item,
          cart_id: null 
        }));
        setCartItems(formattedGuestCart);
      }
    } catch (error) {
      console.error("Failed to fetch cart:", error);
      toast.error("Failed to load your cart");
    }
  };

  // Initialize cart on first render and when auth status changes
  useEffect(() => {
    fetchCart();
  }, [isLoggedIn]);

  // Sync local guest cart to DB after login
  const syncCartFromLocalStorage = async () => {
    try {
      const localCart = JSON.parse(localStorage.getItem("guestCart") || "[]");
      if (localCart.length > 0) {
        const serverCart = await getCart();
        const serverCartItems = serverCart?.cartDetails || [];

        // Improved item comparison with size normalization
        const itemsToAdd = localCart.filter(localItem => {
          const normalizedLocalSize = (localItem.size || "").trim();
          return !serverCartItems.some(serverItem => {
            const normalizedServerSize = (serverItem.size || "").trim();
            return (
              serverItem.product_id === localItem.product_id &&
              normalizedServerSize === normalizedLocalSize
            );
          });
        });

        // Add new items to server cart
        for (const item of itemsToAdd) {
          try {
            await addToCartAPI(item);
          } catch (error) {
            console.error(`Failed to add item ${item.product_id} to cart:`, error);
          }
        }

        localStorage.removeItem("guestCart");
        fetchCart(); // Refresh cart after sync
        // toast.success("Cart items synced successfully");
      }
    } catch (error) {
      console.error("Error syncing cart:", error);
      toast.error("Failed to sync cart items");
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      syncCartFromLocalStorage();
    }
  }, [isLoggedIn]);

  // Add to Cart with improved error handling and state management
  const addToCart = async (product, quantity, size) => {
    if (!size) return { status: "error", message: "Size not selected" };

    const normalizedSize = size.trim();
    const existingItemIndex = cartItems.findIndex(
      item =>
        item.product_id === product.product_id &&
        (item.size || "").trim() === normalizedSize
    );

  
//     if (existingItemIndex !== -1) {
//   return { status: "exists", message: "Product already in cart" };
// }

if (existingItemIndex !== -1) {
  const existingItem = cartItems[existingItemIndex];

  // Max 5 quantity per product
  if (existingItem.quantity >= 5) {
    return { status: "limit", message: "Max 5 quantity reached" };
  }

  const updatedCart = cartItems.map((item, idx) => 
    idx === existingItemIndex
      ? { ...item, quantity: item.quantity + quantity }
      : item
  );

  setCartItems(updatedCart);

  if (!isLoggedIn) {
    localStorage.setItem("guestCart", JSON.stringify(updatedCart));
    return { status: "updated", message: "Quantity increased" };
  }

  try {
    const res = await updateCartQuantityAPI({
      product_id: existingItem.product_id,
      size: existingItem.size,
      change: 1, // or quantity if you're adding more than 1
    });

    if (!res.status) throw new Error(res.message);

    return { status: "updated", message: "Quantity increased" };
  } catch (err) {
    fetchCart();
    return { status: "error", message: "Failed to update quantity" };
  }
}


    // Check cart limit
    // if (cartItems.length >= 5) {
    //   return { status: "limit", message: "Max 5 products in cart" };
    // }

    // Create new item
    const newItem = {
      product_id: product.product_id,
      product_name: product.product_name,
      image: product.image,
      price: product.price,
      quantity,
      size: normalizedSize,
      cart_id: null, // Will be set by server for logged-in users
    };

    const updatedCart = [...cartItems, newItem];
    setCartItems(updatedCart); // Optimistic update

    if (isLoggedIn) {
      try {
        const response = await addToCartAPI(newItem);
        // Update local state with server-assigned cart_id
        if (response.cart_id) {
          const serverUpdatedCart = updatedCart.map(item => 
            item.product_id === newItem.product_id && item.size === newItem.size
              ? { ...item, cart_id: response.cart_id }
              : item
          );
          setCartItems(serverUpdatedCart);
        }
        return { status: "added", message: "Product added to cart" };
      } catch (error) {
        console.error("Error adding to cart:", error);
        fetchCart(); // Revert to server state on error
        return { status: "error", message: "Failed to add to cart" };
      }
    } else {
      localStorage.setItem("guestCart", JSON.stringify(updatedCart));
      return { status: "added", message: "Product added to cart" };
    }
  };

  // Improved deleteCartItem with better error handling and state management
  const deleteCartItem = async (product_id, size) => {
    const normalizedSize = (size || "").trim();
    
    // Optimistic update
    const updatedCart = cartItems.filter(item => 
      !(item.product_id === product_id && (item.size || "").trim() === normalizedSize)
    );
    setCartItems(updatedCart);

    if (!isLoggedIn) {
      localStorage.setItem("guestCart", JSON.stringify(updatedCart));
      return;
    }

   
    // const itemToDelete = cartItems.find(item => 
    //   item.product_id === product_id && (item.size || "").trim() === normalizedSize
    // );

    // const effectiveCartId = cart_id || itemToDelete?.cart_id;
    // if (!effectiveCartId) {
    //   console.error("No cart_id available for deletion");
    //   fetchCart(); 
    //   toast.error("Failed to remove item");
    //   return;
    // }

    try {
      // const res = await deleteCart(product_id, effectiveCartId);
      const res = await deleteCart({ product_id, size: normalizedSize });
      if (res?.status !== "success") {
        throw new Error(res?.message || "Failed to delete item");
      }
      toast(
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ fontSize: "20px" }}>🗑️</span>

          <div>
            <div style={{ fontWeight: 600, color: "#111827" }}>
              Item Removed
            </div>

            <div style={{ fontSize: "13px", color: "#6B7280" }}>
              The item has been removed from your cart.
            </div>
          </div>
        </div>,
        {
          autoClose: 2000,
          hideProgressBar: true,
          closeButton: false,
          icon: false,
          style: {
            background: "#fff",
            color: "#111827",
            borderRadius: "12px",
            padding: "14px 16px",
            border: "1px solid #E5E7EB",
            boxShadow: "0 8px 25px rgba(0,0,0,0.08)",
            minWidth: "320px",
          },
        }
      );
    } catch (error) {
      console.error("Failed to delete cart item:", error);
      fetchCart(); 
      toast.error(error.message || "Failed to remove item");
    }
  };

  const adjustQuantity = async (item, change) => {
    const normalizedSize = (item.size || "").trim();
    if (!item.product_id || typeof item.quantity !== "number") {
      toast.error("Invalid cart item.");
      return;
    }

    const newQuantity = item.quantity + change;
    if (newQuantity < 1 || newQuantity > 5) return;

    const updatedCart = cartItems.map(cartItem =>
      cartItem.product_id === item.product_id && 
      (cartItem.size || "").trim() === normalizedSize
        ? { ...cartItem, quantity: newQuantity }
        : cartItem
    );
    setCartItems(updatedCart);

    if (!isLoggedIn) {
      localStorage.setItem("guestCart", JSON.stringify(updatedCart));
      return;
    }

    try {
      const res = await updateCartQuantityAPI({
        product_id: item.product_id,
        size: normalizedSize,
        change,
      });

      if (!res.status) {
        throw new Error(res.message || "Failed to update quantity");
      }
    } catch (error) {
      console.error("Error updating quantity:", error);
      fetchCart(); 
      toast.error(error.message || "Failed to update quantity");
    }
  };

const increaseQuantity = async (item) => {
  if (item.quantity >= 5) {
    toast.warning("You can add only max 5 quantity");
    return;
  }

  await adjustQuantity(item, 1);
  showQuantityToast(item.product_name, item.quantity + 1);
};


const decreaseQuantity = async (item) => {
  if (item.quantity > 1) {
    await adjustQuantity(item, -1);
    showQuantityToast(item.product_name, item.quantity - 1);
  }
};

  const clearCart = () => {
    setCartItems([]);
    if (!isLoggedIn) {
      localStorage.removeItem("guestCart");
    } else {
      // For logged-in users, you might want to implement a clear cart API call
      // and then call fetchCart() after successful clearance
    }
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        clearCart,
        deleteCartItem,
        increaseQuantity,
        decreaseQuantity,
        // cartCount: cartItems.reduce((total, item) => total + item.quantity, 0),
         cartCount : cartItems.length,
        cartTotal: cartItems.reduce(
          (total, item) => total + item.price * item.quantity,
          0
        ),
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);